import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';

import {
    useForm,
    CalculatorStepAccordion,
    Question,
} from '@ratehub/base-ui';
import { SCENARIO_TYPES } from '@ratehub/mtg-common';

import getMortgageScenarioChooserOptionsPayment from '../functions/getMortgageScenarioChooserOptionsPayment';
import getSelectedPaymentCalculatorScenario from '../functions/getSelectedPaymentCalculatorScenario';
import CalculatorStepInterestRateRisk from './CalculatorStepInterestRateRisk';


function PaymentCalculatorStepInterestRateRisk({
    stepNumber,
    scenarioType,
    scenarioCount,
    onToggleExpand,
    onChangeScenario,
    ...otherProps
}) {
    const form = useForm();
    const isNarrow = form.values.size.width < 780;

    return (
        <CalculatorStepAccordion
            accordionDataName={`calculator-step[${stepNumber}].mtg-paymentCalculator-interestRateRiskAccordion`}
            title={(
                <FormattedMessage
                    id="mtg-calculators.PaymentCalculatorStepInterestRateRisk.title"
                    defaultMessage="Interest rate risk"
                />
            )}
            showBadge={false}
            stepNumber={stepNumber}
            value="interestRateRisk"
            valueFormat="CAD0"
            onToggleExpand={onToggleExpand}
            canBeDisabled
            {...otherProps}
        >
            <div
                className={classNames('rh-display-flex', 'payment-calculator-scenario-container', {
                    'is-horizontal': isNarrow,
                })}
            >
                <div className={classNames('sidebar', isNarrow ? 'rh-pb-1 rh-pt-0' : 'rh-pt-0_5 rh-pr-2')}>
                    <p className="rh-mt-0">
                        <FormattedMessage
                            id="mtg-calculators.PaymentCalculatorStepInterestRateRisk.introduction"
                            defaultMessage="When determining the size of home you can afford, it’s important to look at the long term horizon. The mortgage rate you pay today could be substantially different form the mortgage rates available when the time comes to renew your mortgage."
                        />
                    </p>

                    <p className="rh-mt-0">
                        <FormattedMessage
                            id="mtg-calculators.PaymentCalculatorStepInterestRateRisk.introduction2"
                            defaultMessage="The calculation below shows how much of your mortgage principal will be left at the end of the term."
                        />
                    </p>

                    <If condition={scenarioCount > 1}>
                        <Question
                            id={`scenarioOptions-${stepNumber}`}
                            type="dropdown"
                            field={{
                                name: 'scenarioOptions',
                                type: 'choice',
                                options: getMortgageScenarioChooserOptionsPayment(scenarioType, scenarioCount),
                                value: getSelectedPaymentCalculatorScenario(
                                    form.values.selectedScenarioNumber,
                                    scenarioCount,
                                ),
                            }}
                            onChange={onChangeScenario}
                            labelMessage={(
                                <FormattedMessage
                                    id="calculators.PaymentCalculatorInterestRateRisk.downPaymentOptions.label"
                                    defaultMessage="Down payment options"
                                />
                            )}
                        />
                    </If>
                </div>

                <If condition={!isNarrow}>
                    <div className={classNames('rh-bg-stone-dark', {
                        'rh-height-1px rh-width-100p': isNarrow,
                        'rh-height-auto rh-width-1px': !isNarrow,
                    })}
                    />
                </If>

                <div className="calculations">
                    <CalculatorStepInterestRateRisk />
                </div>
            </div>
        </CalculatorStepAccordion>
    );
}

PaymentCalculatorStepInterestRateRisk.propTypes = {
    scenarioType: PropTypes.oneOf(Object.values(SCENARIO_TYPES)),
    stepNumber: PropTypes.number.isRequired,
    scenarioCount: PropTypes.number.isRequired,
    onChangeScenario: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func,
    className: PropTypes.string,
};

PaymentCalculatorStepInterestRateRisk.defaultProps = {
    scenarioType: SCENARIO_TYPES.PURCHASE,
    onToggleExpand: undefined,
    className: undefined,
};

export default styled(observer(PaymentCalculatorStepInterestRateRisk))`
    .payment-calculator-scenario-container {
        flex-direction: row;

        &.is-horizontal {
            flex-direction: column;
        }

        > .sidebar {
            flex: 1;
        }

        > .calculations {
            flex: 2;
        }
    }
`;
